import React, { useState } from 'react';
import Modal from './common/Modal';
import UpgradePromptModal from './UpgradePromptModal';
import { buildApiUrl } from '../lib/api';
import useLocale from '../hooks/useLocale';
import { resolveUserIdentity } from '../utils/userIdentity';
import '../styles/common/Modal.css';

const CREATE_SCORE_TEXT = {
  ja: {
    validationTitle: '入力エラー',
    validationMessage: 'スコア作成にはタイトルが必要です。',
    createdTitle: '作成完了',
    createdMessage: 'スコアを作成しました。',
    limitReachedTitle: '上限に達しました',
    limitReachedMessage: '無料プランの作成上限（{limit}件）に達しています。PROプランをご検討ください。',
    errorTitle: 'エラー',
    errorMessage: 'スコア作成に失敗しました。再度お試しください。',
    modalTitle: 'スコア作成',
    titleLabel: 'タイトル',
    artistLabel: 'アーティスト',
    cancel: 'キャンセル',
    creating: '作成中...',
    create: '作成',
  },
  en: {
    validationTitle: 'Validation',
    validationMessage: 'Title is required to create a score.',
    createdTitle: 'Created',
    createdMessage: 'Score created successfully.',
    limitReachedTitle: 'Limit reached',
    limitReachedMessage: 'You have reached the FREE plan limit ({limit} scores). Please upgrade to PRO.',
    errorTitle: 'Error',
    errorMessage: 'Failed to create score. Please try again.',
    modalTitle: 'Create Score',
    titleLabel: 'Title',
    artistLabel: 'Artist',
    cancel: 'Cancel',
    creating: 'Creating...',
    create: 'Create',
  },
};

const CreateScoreModal = ({ onClose, onNotify, onCreated }) => {
  const locale = useLocale();
  const text = CREATE_SCORE_TEXT[locale] || CREATE_SCORE_TEXT.en;
  const [scoreName, setScoreName] = useState('');
  const [artistName, setArtistName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const resolveLimitMessage = (limit) => {
    const template = text.limitReachedMessage || CREATE_SCORE_TEXT.en.limitReachedMessage;
    return template.replace('{limit}', String(limit || 3));
  };

  const promptUpgrade = () => {
    setIsUpgradeModalOpen(true);
  };

  const handleCreate = async () => {
    if (submitting) return;
    const identity = resolveUserIdentity();
    const userId = identity.userId;
    if (!userId || !scoreName.trim()) {
      onNotify?.({ title: text.validationTitle, message: text.validationMessage, variant: 'error' });
      return;
    }
    const scoreId = crypto.randomUUID();

    try {
      setSubmitting(true);
      const response = await fetch(buildApiUrl('/create-score'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          email: identity.email,
          title: scoreName.trim(),
          artist: artistName.trim(),
          scoreId,
        }),
      });

      if (!response.ok) {
        const responseBody = await response.json().catch(() => ({}));
        if (response.status === 403 && responseBody?.message === 'FREE_SCORE_LIMIT_REACHED') {
          onNotify?.({
            title: text.limitReachedTitle,
            message: resolveLimitMessage(responseBody?.limit),
            variant: 'error',
          });
          promptUpgrade();
          return;
        }
        throw new Error(responseBody?.message || `HTTP ${response.status}`);
      }

      await response.json().catch(() => ({}));
      onNotify?.({ title: text.createdTitle, message: text.createdMessage, variant: 'success' });
      onCreated?.();
      onClose?.();
    } catch (error) {
      console.error('Error creating score:', error);
      onNotify?.({ title: text.errorTitle, message: text.errorMessage, variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        contentClassName="modal-content--modern"
        contentProps={{ role: 'dialog', 'aria-modal': true }}
      >
        <div className="modal-header">
          <h2>{text.modalTitle}</h2>
        </div>
        <div className="modal-body">
          <label className="modal-field-label">{text.titleLabel}</label>
          <input
            type="text"
            value={scoreName}
            onChange={(e) => setScoreName(e.target.value)}
          />
          <label className="modal-field-label" style={{ marginTop: 12 }}>{text.artistLabel}</label>
          <input
            type="text"
            value={artistName}
            onChange={(e) => setArtistName(e.target.value)}
          />
        </div>
        <div className="modal-footer modal-buttons">
          <button className="cancel" onClick={onClose} disabled={submitting}>{text.cancel}</button>
          <button className="create" onClick={handleCreate} disabled={submitting}>
            {submitting ? text.creating : text.create}
          </button>
        </div>
      </Modal>
      <UpgradePromptModal
        isOpen={isUpgradeModalOpen}
        locale={locale}
        onClose={() => setIsUpgradeModalOpen(false)}
        onConfirm={() => {
          setIsUpgradeModalOpen(false);
          window.location.href = '/upgrade';
        }}
      />
    </>
  );
};

export default CreateScoreModal;
